import { useTranslation } from 'react-i18next'
import { useState, useMemo, useContext, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { Table, DatePicker } from 'antd'

import { UserContext } from '../../hooks/UserContext'
import { PageView } from '../../components/PageView'
import { PageTopBar } from '../../components/PageTopBar'
import { SelectInput } from '../../components/SelectInput'
import { Button } from '../../components/Button'
import { Loader } from '../../components/Loader'
import { Notification } from '../../components/Notification'
import { FormattedDate } from '../../components/FormattedDate'
import { listUberBatches, submitPendingUberBatches } from '../../services'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { UBER_BATCH_DETAIL } from '../../navigation/constants'
import { UBER_BATCH_STATUSES } from './uberBatchStatus'
import { UberBatchStatusPill } from './UberBatchStatusPill'

const { RangePicker } = DatePicker

const T_PREFIX = 'uber_batches'

const REFETCH_INTERVAL_MS = 30000

const ALL_OPTION_ID = '__all__'

export const UberBatchesList = ({ embedded = false } = {}) => {
    const { i18n } = useTranslation()
    const { user } = useContext(UserContext)
    const { warehouse_id } = useParams()
    const history = useHistory()

    const hasAccess = !!user?.permissions?.index_uber_batches

    const [filters, setFilters] = useState({
        status: null,
        created_from: null,
        created_to: null,
    })
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [notification, setNotification] = useState(null)

    const searchParams = useMemo(() => {
        const params = {
            warehouse_id,
            page,
            per_page: perPage,
        }
        if (filters.status) params.status = filters.status
        if (filters.created_from) params.created_from = filters.created_from
        if (filters.created_to) params.created_to = filters.created_to
        return params
    }, [filters, page, perPage, warehouse_id])

    const { data: warehouse } = useQuery(
        ['warehouse', warehouse_id],
        () => fetchWarehouse(warehouse_id),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    )

    const {
        data,
        isLoading,
        isError,
        error,
        isFetching,
        isPreviousData,
        refetch,
    } = useQuery(
        ['uber_batches', searchParams],
        () => listUberBatches(searchParams),
        {
            keepPreviousData: true,
            // Background polling — React Query keeps the previous data on screen
            // (keepPreviousData) so the table doesn't flicker while the user is
            // mid-interaction (open dropdowns, scrolling, etc).
            refetchInterval: REFETCH_INTERVAL_MS,
            refetchOnWindowFocus: false,
            enabled: hasAccess,
        }
    )

    const { mutate: submitPending, isLoading: isSubmitting } = useMutation(
        () => submitPendingUberBatches(parseInt(warehouse_id, 10)),
        {
            onSuccess: (data) => {
                setNotification({
                    type: 'success',
                    title: i18n.t(`${T_PREFIX}.actions.submit_pending_success_title`),
                    description: i18n.t(`${T_PREFIX}.actions.submit_pending_success_description`, {
                        count: data.pending_count,
                    }),
                })
                refetch()
            },
            onError: (e) => {
                const msg = e?.response?.data?.error || i18n.t(`${T_PREFIX}.errors.submit_error`)
                setNotification({
                    type: 'error',
                    title: i18n.t(`${T_PREFIX}.errors.submit_error_title`),
                    description: msg,
                })
            },
        }
    )

    const statusOptions = useMemo(() => [
        { id: ALL_OPTION_ID, name: i18n.t(`${T_PREFIX}.filters.all_statuses`) },
        ...UBER_BATCH_STATUSES.map((s) => ({ id: s, name: i18n.t(`${T_PREFIX}.status.${s}`) })),
    ], [i18n])

    const selectedStatus = statusOptions.find((o) => o.id === filters.status) || statusOptions[0]

    const handleStatusChange = (value) => {
        const val = value.id === ALL_OPTION_ID ? null : value.id
        setFilters((prev) => ({ ...prev, status: val }))
        setPage(1)
    }

    const handleDateRangeChange = (dates) => {
        if (!dates || dates.length === 0) {
            setFilters((prev) => ({ ...prev, created_from: null, created_to: null }))
        } else {
            setFilters((prev) => ({
                ...prev,
                created_from: dates[0]?.toISOString() || null,
                created_to: dates[1]?.toISOString() || null,
            }))
        }
        setPage(1)
    }

    const handleResetFilters = () => {
        setFilters({ status: null, created_from: null, created_to: null })
        setPage(1)
    }

    const formatWindow = useCallback((batch) => {
        if (!batch.dispatch_window_start && !batch.dispatch_window_end) return '-'
        return (
            <div className="text-sm text-gray-700 whitespace-nowrap">
                <FormattedDate date={batch.dispatch_window_start} shortDate />
                {' '}—{' '}
                <FormattedDate date={batch.dispatch_window_end} shortDate />
            </div>
        )
    }, [])

    const columns = useMemo(() => [
        {
            title: i18n.t(`${T_PREFIX}.headers.id`),
            dataIndex: 'id',
            key: 'id',
            render: (id) => <span className="font-mono text-sm">#{id}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.warehouse`),
            dataIndex: 'warehouse_name',
            key: 'warehouse_name',
            render: (_, batch) => (
                <span className="text-sm text-gray-700">
                    {batch.warehouse_name || warehouse?.name || `#${batch.warehouse_id}`}
                </span>
            ),
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.status`),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <UberBatchStatusPill status={status} />,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.internal_label`),
            dataIndex: 'internal_label',
            key: 'internal_label',
            render: (label, batch) => label ? (
                <div>
                    <span className="font-mono text-sm">{label}</span>
                    {batch.uber_batch_id && (
                        <div className="text-xs text-gray-500 font-mono">{batch.uber_batch_id}</div>
                    )}
                </div>
            ) : <span className="text-gray-400">-</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.dispatch_window`),
            key: 'dispatch_window',
            render: (_, batch) => formatWindow(batch),
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.deliveries_count`),
            dataIndex: 'deliveries_count',
            key: 'deliveries_count',
            render: (count) => <span className="text-sm">{count ?? 0}</span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.submitted_at`),
            dataIndex: 'submitted_at',
            key: 'submitted_at',
            render: (date) => <span className="text-sm text-gray-600"><FormattedDate date={date} shortDate /></span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.failed_at`),
            dataIndex: 'failed_at',
            key: 'failed_at',
            render: (date) => <span className="text-sm text-gray-600"><FormattedDate date={date} shortDate /></span>,
        },
        {
            title: i18n.t(`${T_PREFIX}.headers.actions`),
            key: 'actions',
            render: (_, batch) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        const target = UBER_BATCH_DETAIL
                            .replace(':warehouse_id', warehouse_id)
                            .replace(':id', batch.id)
                        history.push(target)
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                >
                    {i18n.t(`${T_PREFIX}.actions.view`)}
                </button>
            ),
        },
    ], [i18n, warehouse_id, history, warehouse, formatWindow])

    const batches = useMemo(() => {
        // The endpoint returns either a bare array or { uber_batches, meta }
        // (different platform endpoints follow different conventions). Support both.
        if (Array.isArray(data)) return data
        if (Array.isArray(data?.uber_batches)) return data.uber_batches
        if (Array.isArray(data?.data)) return data.data
        return []
    }, [data])

    const meta = data?.meta

    if (!hasAccess) {
        const noAccess = (
            <div className="text-center py-12">
                <p className="text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</p>
            </div>
        )
        return embedded ? noAccess : <PageView>{noAccess}</PageView>
    }

    const content = (
        <>
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-56">
                        <SelectInput
                            label={i18n.t(`${T_PREFIX}.filters.status`)}
                            options={statusOptions}
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            buttonClassName={filters.status ? 'border-indigo-400 border-2' : ''}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {i18n.t(`${T_PREFIX}.filters.created_at`)}
                        </label>
                        <RangePicker
                            showTime={false}
                            onChange={handleDateRangeChange}
                            allowClear
                        />
                    </div>
                    <div>
                        <Button onClick={handleResetFilters} type="secondary">
                            {i18n.t(`${T_PREFIX}.filters.reset`)}
                        </Button>
                    </div>
                    <div className="ml-auto flex gap-2">
                        <Button
                            onClick={() => refetch()}
                            type="secondary"
                            disabled={isFetching}
                        >
                            {i18n.t(`${T_PREFIX}.actions.reload`)}
                        </Button>
                        <Button
                            onClick={() => submitPending()}
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            {i18n.t(`${T_PREFIX}.actions.submit_pending`)}
                        </Button>
                    </div>
                </div>
            </div>

            {(!isPreviousData && isLoading) ? (
                <Loader show={true} />
            ) : isError ? (
                <div className="text-center py-12">
                    <p className="text-red-500">
                        {error?.message || i18n.t(`${T_PREFIX}.errors.fetch_error`)}
                    </p>
                </div>
            ) : (
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={batches}
                    loading={isFetching && !isPreviousData}
                    pagination={{
                        current: page,
                        pageSize: perPage,
                        total: meta?.total_count || meta?.total || batches.length,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        onChange: (newPage, newPerPage) => {
                            setPage(newPage)
                            setPerPage(newPerPage)
                        },
                    }}
                    locale={{ emptyText: i18n.t(`${T_PREFIX}.empty_table`) }}
                />
            )}
        </>
    )

    const notificationEl = (
        <Notification
            show={!!notification}
            setShow={() => setNotification(null)}
            type={notification?.type || 'success'}
            title={notification?.title || ''}
            description={notification?.description || ''}
        />
    )

    if (embedded) return <>{content}{notificationEl}</>

    return (
        <>
            <PageView
                topMenu={
                    <PageTopBar>
                        <div className="text-lg font-semibold">
                            {i18n.t(`${T_PREFIX}.list_title`)}
                        </div>
                    </PageTopBar>
                }
                childrenFullWidth={true}
                topMenuFullWidth={true}
            >
                {content}
            </PageView>
            {notificationEl}
        </>
    )
}
